import { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PageHeader from "@/components/PageHeader";
import AlertsPanel from "@/components/AlertsPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/lib/auth";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle,
  AlertDialogDescription, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  KeyRound, Save, ShieldCheck, Bell, Lock, Download, Monitor,
  TrendingUp, CheckCircle, Clock, RotateCcw, Wallet
} from "lucide-react";

// ── RU-G32B-2: live derivative entry switches (admin only) ─────────────────────
// Both must be ON for a live Zerodha option entry; turning them on is the owner's
// action, recorded in the ledger. The backend enforces admin on its own as well.
const DX_SWITCHES = [
  { key: "live_derivative_entries", label: "Live derivative entries",
    help: "Arms option buy-to-open entries from signal Execute (1 lot, LIMIT IOC) for Zerodha (with Zerodha F&O "
      + "auto-close), Dhan (with Dhan F&O auto-close), AngelOne and Upstox.",
    url: "/risk/live-derivative-entries" },
  { key: "zerodha_fno_autoclose", label: "Zerodha F&O auto-close",
    help: "Exact-contract stop/target closes of Zerodha F&O brackets (the entries' exit path).",
    url: "/risk/zerodha-fno-autoclose" },
  // RU-DHANREADY: its own GET/PUT route; its state is read from GET /risk/dhan-fno-autoclose
  { key: "dhan_fno_autoclose", label: "Dhan F&O auto-close",
    help: "Exact-contract stop/target closes of Dhan F&O brackets (required ON with live derivative entries "
      + "for a Dhan option entry).",
    url: "/risk/dhan-fno-autoclose" },
];

function _lastChangeText(lc) {
  if (!lc) return "never changed";
  const when = lc.at ? new Date(lc.at).toLocaleString("en-IN") : "?";
  return `${lc.to ? "ON" : "OFF"} by ${lc.by || "?"} · ${when} · “${lc.reason || ""}”`;
}

export function LiveDerivativeSwitchesPanel() {
  const [state, setState] = useState(null);
  const [pending, setPending] = useState(null);   // { sw, to }
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  function load() {
    // RU-DHANREADY G7 F1: a failed Dhan read never hides the Zerodha switches (allSettled); the Dhan switch
    // is then shown unreadable and cannot be changed.
    Promise.allSettled([api.get("/risk/live-derivative-entries"), api.get("/risk/dhan-fno-autoclose")])
      .then(([rs, ds]) => {
        if (rs.status !== "fulfilled") { setState({ error: true }); return; }
        const r = rs.value, d = ds.status === "fulfilled" ? ds.value : null;
        setState({
          ...r.data,
          switches: { ...(r.data?.switches || {}),
                      dhan_fno_autoclose: d ? { enabled: d.data?.enabled, last_change: d.data?.last_change }
                                            : { enabled: null, unreadable: true } },
          dhan_can_change: d ? d.data?.can_change === true : false,
          dhan_open_pilot_brackets: d ? (d.data?.open_pilot_brackets || []) : [],
        });
      });
  }
  useEffect(() => { load(); }, []);

  async function confirm() {
    if (!pending || !reason.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.put(pending.sw.url, { enabled: pending.to, reason: reason.trim() });
      if (data?.warning) {
        const list = (data.open_pilot_brackets || []).map((b) => b.contract_symbol || b.symbol).join(", ");
        toast.warning(`${data.warning}${list ? `: ${list}` : ""}`, { duration: 15000 });
      } else {
        toast.success(`${pending.sw.label} ${pending.to ? "ON" : "OFF"}`);
      }
      setPending(null);
      setReason("");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Change refused");
    } finally {
      setBusy(false);
    }
  }

  if (!state) return <div className="txt-muted text-xs">Loading…</div>;
  if (state.error) return <div className="text-red-400 text-xs">Switch state could not be read.</div>;
  const isDhan = pending?.sw.key === "dhan_fno_autoclose";
  const pilot = (isDhan ? state.dhan_open_pilot_brackets : state.open_pilot_brackets) || [];
  const turningAutocloseOff = pending && (pending.sw.key === "zerodha_fno_autoclose" || isDhan) && !pending.to;

  return (
    <div className="space-y-4" data-testid="live-derivative-switches">
      {DX_SWITCHES.map((sw) => {
        const cur = state.switches?.[sw.key] || {};
        return (
          <div key={sw.key} className="flex items-start justify-between gap-4 border-t border-[var(--border)] pt-4">
            <div>
              <div className="overline">{sw.label}</div>
              <div className="text-xs txt-muted mt-1">{sw.help}</div>
              <div className="text-[11px] font-mono-data txt-secondary mt-1" data-testid={`dx-last-${sw.key}`}>
                {cur.unreadable ? "State could not be read" : <>Last change: {_lastChangeText(cur.last_change)}</>}
              </div>
            </div>
            <Switch
              data-testid={`dx-switch-${sw.key}`}
              checked={!!cur.enabled}
              disabled={sw.key === "dhan_fno_autoclose" ? !state.dhan_can_change : !state.can_change}
              onCheckedChange={(v) => { setReason(""); setPending({ sw, to: v }); }}
            />
          </div>
        );
      })}
      <div className="text-[11px] txt-muted">
        A live Zerodha or Dhan derivative entry needs live derivative entries AND that broker&apos;s auto-close ON.
        Open pilot brackets: Zerodha {(state.open_pilot_brackets || []).length}, Dhan {(state.dhan_open_pilot_brackets || []).length}
      </div>
      <AlertDialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Turn {pending?.sw.label} {pending?.to ? "ON" : "OFF"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This changes live order behaviour and is recorded with your reason (HIGH audit).
            </AlertDialogDescription>
          </AlertDialogHeader>
          {turningAutocloseOff && pilot.length > 0 && (
            <div className="text-xs text-amber-300 border border-amber-500/40 p-2" data-testid="dx-autoclose-warning">
              {pilot.length} open live derivative entry bracket(s) will NOT be closed automatically while auto-close
              is off: {pilot.map((b) => `${b.contract_symbol || b.symbol} (${b.qty}, ${b.status})`).join(", ")}
            </div>
          )}
          <div>
            <Label className="overline">Reason (required)</Label>
            <Input data-testid="dx-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                   maxLength={500} className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)]" />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <Button data-testid="dx-confirm" onClick={confirm} disabled={busy || !reason.trim()}
                    className="rounded-none">
              {busy ? "Saving…" : "Confirm"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ── Password Change Panel ──────────────────────────────────────────────────────

function PasswordPanel() {
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (form.new_password.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (form.new_password !== form.confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password changed successfully");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to change password");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="panel p-6 max-w-md">
      <div className="flex items-center gap-2 mb-4">
        <Lock className="w-4 h-4 txt-muted" />
        <div className="overline">Change Password</div>
      </div>
      <div className="space-y-4">
        <div>
          <Label className="overline text-xs">Current password</Label>
          <Input
            type="password"
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)]"
            autoComplete="current-password"
          />
        </div>
        <div>
          <Label className="overline text-xs">New password</Label>
          <Input
            type="password"
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)]"
            autoComplete="new-password"
          />
        </div>
        <div>
          <Label className="overline text-xs">Confirm new password</Label>
          <Input
            type="password"
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)]"
            autoComplete="new-password"
          />
        </div>
        <Button
          onClick={submit}
          disabled={saving || !form.current_password || !form.new_password}
          className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider w-full h-10"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "SAVING…" : "CHANGE PASSWORD"}
        </Button>
      </div>
    </div>
  );
}

// ── Promotion Request (signal_user only) ──────────────────────────────────────

function PromotionPanel({ user }) {
  const [req, setReq] = useState(null);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/auth/my-promotion-request").then((r) => setReq(r.data)).finally(() => setLoading(false));
  }, []);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/auth/request-promotion", { reason });
      toast.success("Promotion request submitted — admin will review shortly");
      const r = await api.get("/auth/my-promotion-request");
      setReq(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <div className="panel p-6 max-w-md">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-4 h-4 txt-muted" />
        <div className="overline">Request Trader Access</div>
      </div>

      {req?.exists && req.status === "pending" && (
        <div className="flex items-start gap-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-none mb-4">
          <Clock className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
          <div>
            <div className="text-sm font-section text-yellow-300">Request pending review</div>
            <div className="text-xs txt-muted mt-1">Submitted {req.created_at?.slice(0, 10)}. Admin will approve or reject soon.</div>
          </div>
        </div>
      )}

      {req?.exists && req.status === "approved" && (
        <div className="flex items-start gap-3 p-3 bg-green-500/10 border border-green-500/30 rounded-none">
          <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
          <div className="text-sm font-section text-green-300">Approved — your account has been upgraded to Trader. Please log out and log in again to activate new permissions.</div>
        </div>
      )}

      {req?.exists && req.status === "rejected" && (
        <div className="text-sm txt-muted">Your last request was not approved. You may submit a new one below.</div>
      )}

      {(!req?.exists || req?.status === "rejected") && (
        <>
          <p className="txt-secondary text-sm mb-4">
            Signal Users receive live signals but cannot place paper or live orders. Request an upgrade to Trader to unlock the full platform.
          </p>
          <div className="mb-4">
            <Label className="overline text-xs">Reason (optional)</Label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. I'm an active equity trader and want to use paper trading to test strategies…"
              className="mt-2 w-full bg-[var(--bg-page)] border border-[var(--border)] rounded-none p-3 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-white/20"
            />
          </div>
          <Button
            onClick={submit}
            disabled={submitting}
            className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider w-full h-10"
          >
            {submitting ? "SUBMITTING…" : "REQUEST TRADER ACCESS"}
          </Button>
        </>
      )}
    </div>
  );
}

// ── Desktop App Download Panel ─────────────────────────────────────────────────

const RELEASES_BASE = "https://github.com/dkshaikdxb-dev/AlgoForge-V2/releases/latest/download";

const DOWNLOADS = [
  {
    platform: "Windows",
    icon: "🪟",
    file: "AlgoForge-Setup.exe",
    label: "Download for Windows (.exe)",
    hint: "Windows 10 / 11, 64-bit — NSIS installer",
  },
  {
    platform: "macOS",
    icon: "🍎",
    file: "AlgoForge.dmg",
    label: "Download for macOS (.dmg)",
    hint: "macOS 11+, Intel and Apple Silicon",
  },
  {
    platform: "Linux",
    icon: "🐧",
    file: "AlgoForge.AppImage",
    label: "Download for Linux (.AppImage)",
    hint: "Ubuntu 20.04+ / Debian / Fedora, 64-bit",
  },
  {
    platform: "Android",
    icon: "🤖",
    file: "AlgoForge.apk",
    label: "Download for Android (.apk)",
    hint: "Android 8.0+ (Oreo) — sideload APK",
  },
  {
    platform: "iOS",
    icon: "📱",
    file: null,
    label: "iOS — Coming Soon",
    hint: "App Store release in progress",
    href: null,
    disabled: true,
  },
];

const INSTALL_STEPS = [
  {
    step: "1",
    title: "Download the installer",
    body: "Click your platform's download link above. The file is typically 80–120 MB.",
  },
  {
    step: "2",
    title: "Windows: Run AlgoForge-Setup.exe",
    body: 'If Windows SmartScreen appears, click "More info" → "Run anyway". This is safe — the installer is signed. Accept the default installation path and click Install.',
    platform: "Windows",
  },
  {
    step: "2",
    title: "macOS: Open AlgoForge.dmg",
    body: 'Drag AlgoForge to the Applications folder. On first launch, right-click → Open to bypass Gatekeeper (only required once). Allow network access when prompted.',
    platform: "macOS",
  },
  {
    step: "2",
    title: "Linux: Make AppImage executable",
    body: 'Run: chmod +x AlgoForge.AppImage then ./AlgoForge.AppImage. No root required. Optionally use AppImageLauncher for desktop integration.',
    platform: "Linux",
  },
  {
    step: "3",
    title: "Launch AlgoForge",
    body: "The app opens to algoforge.tech automatically. Log in with your existing account credentials.",
  },
  {
    step: "4",
    title: "SEBI Static IP — why desktop matters",
    body: "All broker API calls go through the AlgoForge VPS (static registered IP). The desktop app is a thin client — your trades are executed from the VPS, satisfying SEBI's requirement for algo systems to operate from a static, pre-registered IP address.",
  },
  {
    step: "5",
    title: "Auto-updates",
    body: "AlgoForge checks for updates on every launch and notifies you when a new version is ready. Click 'Install Update' in the top bar to restart with the latest build.",
  },
  {
    step: "6",
    title: "System tray",
    body: "The app minimises to tray (Windows) or menu bar (macOS) when closed. Right-click the tray icon to Open, change Server URL, or Quit.",
  },
  {
    step: "1",
    title: "Enable unknown sources",
    body: 'Go to Settings → Apps → Special app access → Install unknown apps, then enable your browser or file manager.',
    platform: "Android",
  },
  {
    step: "2",
    title: "Download and install APK",
    body: "Tap the download button above. Once downloaded, tap the file and follow the on-screen prompts. The APK is ~5 MB — it connects to algoforge.tech securely over HTTPS.",
    platform: "Android",
  },
  {
    step: "3",
    title: "Log in with your account",
    body: "Open AlgoForge from your app drawer and log in with your existing credentials. Everything syncs from the server — no data is stored locally.",
    platform: "Android",
  },
  {
    step: "1",
    title: "iOS — App Store release in progress",
    body: "The iOS app is built and ready. It will be available on the App Store once Apple's review process is complete. You'll receive an email notification when it goes live.",
    platform: "iOS",
  },
];

function DownloadPanel() {
  const [platform, setPlatform] = useState("Windows");

  const filteredSteps = INSTALL_STEPS.filter((s) => !s.platform || s.platform === platform);

  return (
    <div className="space-y-6">
      <div className="panel p-6">
        <div className="flex items-center gap-2 mb-4">
          <Monitor className="w-4 h-4 txt-muted" />
          <div className="overline">AlgoForge Desktop</div>
          <span className="ml-auto text-xs txt-muted font-section bg-[var(--bg-surface-2)] px-2 py-0.5 border border-[var(--border)]">SEBI STATIC IP COMPLIANT</span>
        </div>
        <p className="txt-secondary text-sm mb-5">
          The desktop app provides native OS notifications for trade alerts and enables SEBI-compliant algo trading — all broker calls originate from the AlgoForge VPS static IP, not your home connection.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {DOWNLOADS.map((d) =>
            d.disabled ? (
              <div
                key={d.platform}
                className="flex flex-col gap-2 p-4 border border-[var(--border)] opacity-40 cursor-not-allowed"
              >
                <div className="text-2xl">{d.icon}</div>
                <div className="font-section text-sm text-white">{d.label}</div>
                <div className="text-xs txt-muted">{d.hint}</div>
              </div>
            ) : (
              <a
                key={d.platform}
                href={`${RELEASES_BASE}/${d.file}`}
                className="flex flex-col gap-2 p-4 border border-[var(--border)] hover:border-white/40 hover:bg-[var(--bg-surface-2)] transition-colors group"
              >
                <div className="text-2xl">{d.icon}</div>
                <div className="font-section text-sm text-white group-hover:underline">{d.label}</div>
                <div className="text-xs txt-muted">{d.hint}</div>
              </a>
            )
          )}
        </div>
      </div>

      <div className="panel p-6">
        <div className="flex items-center gap-2 mb-4">
          <Download className="w-4 h-4 txt-muted" />
          <div className="overline">Installation Guide</div>
          <div className="ml-auto flex gap-2">
            {["Windows", "macOS", "Linux", "Android", "iOS"].map((p) => (
              <button
                key={p}
                onClick={() => setPlatform(p)}
                className={`text-xs px-3 py-1 font-section border transition-colors ${platform === p ? "border-white text-white bg-[var(--bg-surface-2)]" : "border-[var(--border)] txt-muted hover:border-white/40"}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-4">
          {filteredSteps.map((s, i) => (
            <div key={i} className="flex gap-4">
              <div className="w-7 h-7 rounded-full border border-[var(--border)] flex items-center justify-center text-xs font-section txt-muted shrink-0 mt-0.5">
                {s.step}
              </div>
              <div>
                <div className="text-sm font-section text-white mb-1">{s.title}</div>
                <div className="text-xs txt-secondary leading-relaxed">{s.body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Demo Account Panel ─────────────────────────────────────────────────────────

function DemoAccountPanel() {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);

  const load = () => {
    api.get("/paper/demo-info").then(({ data }) => setInfo(data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const reset = async () => {
    if (!window.confirm("Reset your demo account? All paper positions, orders, and P&L will be cleared and your balance restored to ₹10,00,000.")) return;
    setResetting(true);
    try {
      await api.post("/admin/users/reset-demo-self");
      toast.success("Demo account reset — ₹10,00,000 restored. Refresh Paper Trading to see the change.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const canReset = !info?.demo_reset_at || (Date.now() - new Date(info.demo_reset_at).getTime() > 86400000);

  return (
    <div className="max-w-xl space-y-4">
      <div className="panel p-6">
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="w-4 h-4 txt-muted" />
          <div className="overline">Virtual Trading Account</div>
        </div>
        <p className="txt-secondary text-sm mb-6">
          Your demo account is a fully simulated trading environment with ₹10,00,000 virtual capital.
          Use it to test strategies, practice bracket orders, and explore all features without real money at risk.
        </p>

        {loading ? (
          <div className="txt-muted text-sm">Loading…</div>
        ) : (
          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between py-2 border-b border-[var(--border)]">
              <span className="txt-muted text-xs font-section tracking-wider">CURRENT BALANCE</span>
              <span className="font-section text-white">₹{(info?.demo_balance ?? 1000000).toLocaleString("en-IN")}</span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-[var(--border)]">
              <span className="txt-muted text-xs font-section tracking-wider">LAST RESET</span>
              <span className="txt-secondary text-xs">{info?.demo_reset_at ? new Date(info.demo_reset_at).toLocaleString("en-IN") : "Never"}</span>
            </div>
            <div className="flex items-center justify-between py-2">
              <span className="txt-muted text-xs font-section tracking-wider">NEXT RESET AVAILABLE</span>
              {canReset ? (
                <span className="text-emerald-400 text-xs font-section">Now</span>
              ) : (
                <span className="txt-secondary text-xs">
                  {new Date(new Date(info.demo_reset_at).getTime() + 86400000).toLocaleString("en-IN")}
                </span>
              )}
            </div>
          </div>
        )}

        <Button
          onClick={reset}
          disabled={resetting || !canReset}
          variant="outline"
          className="rounded-none w-full border-purple-500/40 text-purple-400 hover:bg-purple-500/10 hover:border-purple-500/60"
        >
          <RotateCcw className="w-4 h-4 mr-2" />
          {resetting ? "Resetting…" : canReset ? "Reset Demo Account" : "Reset available in 24h"}
        </Button>
        {!canReset && (
          <p className="txt-muted text-xs text-center mt-2">Demo accounts reset once every 24 hours to prevent abuse.</p>
        )}
      </div>

      <div className="panel p-5 border-white/5">
        <div className="overline mb-3 text-xs">What gets reset</div>
        <ul className="space-y-1.5 txt-secondary text-xs">
          {["All paper orders (filled, pending, cancelled)", "All open paper positions", "All active bracket orders", "Virtual P&L history", "Balance restored to ₹10,00,000"].map((item) => (
            <li key={item} className="flex items-center gap-2">
              <CheckCircle className="w-3 h-3 text-emerald-400 flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
        <div className="overline mb-2 mt-4 text-xs">What is preserved</div>
        <ul className="space-y-1.5 txt-secondary text-xs">
          {["Your strategies and backtest results", "Signal history", "Journal entries", "Account settings and broker connections"].map((item) => (
            <li key={item} className="flex items-center gap-2">
              <Clock className="w-3 h-3 txt-muted flex-shrink-0" />
              {item}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// ── RU-LIVELOSSCAP: live daily loss cap (LL-2, LL-17) ──────────────────────────
// The cap is edited here (reason needed to raise or disable it); today's AlgoForge live loss against it is
// read-only. The backend decides and enforces; nothing here changes an order.
const _inr = (n) => (n === null || n === undefined || Number.isNaN(Number(n)))
  ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const _LOSS_STATUS = {
  ok: { label: "Within cap", cls: "txt-profit" },
  breached: { label: "CAP REACHED — new live entries blocked", cls: "txt-loss" },
  unknown: { label: "Loss unknown", cls: "txt-warn" },
  disabled: { label: "Disabled", cls: "txt-muted" },
  error: { label: "Could not be computed", cls: "txt-loss" },
};

export function LiveLossCapPanel() {
  const [data, setData] = useState(null);
  const [cap, setCap] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  function load() {
    api.get("/risk/live-daily-loss-cap")
      .then((r) => { setData(r.data); setCap(String(r.data?.cap ?? "")); })
      .catch(() => setData({ error: true }));
  }
  useEffect(() => { load(); }, []);

  async function save() {
    const value = Number(cap);
    if (cap === "" || !Number.isFinite(value) || value < 0) {
      toast.error("Enter a cap in rupees (0 disables it)");
      return;
    }
    setBusy(true);
    try {
      const { data: out } = await api.put("/risk/live-daily-loss-cap", { cap: value, reason: reason.trim() });
      toast.success(out?.note ? `Live daily loss cap saved (${out.note})` : "Live daily loss cap saved");
      setReason("");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Change refused");
    } finally {
      setBusy(false);
    }
  }

  if (!data) return <div className="txt-muted text-xs">Loading…</div>;
  if (data.error) return <div className="text-red-400 text-xs">The live daily loss cap could not be read.</div>;
  const t = data.today || {};
  const st = _LOSS_STATUS[t.status] || { label: t.status || "—", cls: "txt-muted" };
  const loosening = Number(data.cap) > 0 && cap !== "" && (Number(cap) === 0 || Number(cap) > Number(data.cap));
  const lc = data.last_change;
  return (
    <div className="space-y-4" data-testid="live-loss-cap">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="overline">Live daily loss cap (₹, 0 = off)</Label>
          <Input data-testid="live-loss-cap-input" type="number" min="0" value={cap}
                 onChange={(e) => setCap(e.target.value)}
                 className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data" />
          <div className="text-[11px] txt-muted mt-1">
            AlgoForge live positions only (realised today + open MTM). When reached, new live entries are blocked on
            every path until 00:00 IST; positions are never closed and exits still work.
          </div>
        </div>
        <div>
          <Label className="overline">Reason {loosening ? "(required to raise or disable)" : "(optional)"}</Label>
          <Input data-testid="live-loss-cap-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                 className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)]" />
          <Button data-testid="live-loss-cap-save" onClick={save} disabled={busy || (loosening && !reason.trim())}
                  className="mt-2 rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider w-full h-10">
            <Save className="w-4 h-4 mr-2" /> {busy ? "SAVING…" : "SAVE CAP"}
          </Button>
        </div>
      </div>
      <div className="border-t border-[var(--border)] pt-4" data-testid="live-loss-today">
        <div className="overline mb-2">Today (IST {t.day || "—"}) · read-only</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm font-mono-data">
          <div><div className="text-[11px] txt-muted">Loss / cap</div>
            <div data-testid="live-loss-vs-cap">{_inr(t.loss)} / {_inr(t.cap ?? data.cap)}</div></div>
          <div><div className="text-[11px] txt-muted">Realised today</div><div>{_inr(t.realised)}</div></div>
          <div><div className="text-[11px] txt-muted">Open MTM</div><div>{_inr(t.open_mtm)}</div></div>
          <div><div className="text-[11px] txt-muted">Status</div>
            <div className={st.cls} data-testid="live-loss-status">{st.label}</div></div>
        </div>
        {t.reason && <div className="text-xs txt-loss mt-2" data-testid="live-loss-reason">{t.reason}</div>}
        {(t.unknown || []).length > 0 && (
          <div className="text-[11px] txt-warn mt-2" data-testid="live-loss-unknown">
            Unknown{t.unknown_since ? ` since ${new Date(t.unknown_since).toLocaleTimeString("en-IN")}` : ""}:{" "}
            {t.unknown.slice(0, 3).join("; ")} (new entries are blocked after 5 minutes unknown)
          </div>
        )}
        {(t.notes || []).length > 0 && (
          <div className="text-[11px] txt-muted mt-1">{t.notes.slice(0, 3).join("; ")}</div>
        )}
      </div>
      <div className="text-[11px] font-mono-data txt-secondary" data-testid="live-loss-last-change">
        Last change: {lc ? `${_inr(lc.from)} → ${_inr(lc.to)} by ${lc.by || "?"} · `
          + `${lc.at ? new Date(lc.at).toLocaleString("en-IN") : "?"} · “${lc.reason || ""}”` : `never changed (default ${_inr(data.default)})`}
      </div>
    </div>
  );
}

// ── Main Settings page ─────────────────────────────────────────────────────────

export default function Settings() {
  const { user } = useAuth();
  const [risk, setRisk] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/risk/limits").then((r) => setRisk(r.data));
  }, []);

  const save = async () => {
    if (!risk) return;
    setSaving(true);
    try {
      await api.put("/risk/limits", {
        max_drawdown_pct: Number(risk.max_drawdown_pct),
        daily_loss_cap: Number(risk.daily_loss_cap),
        position_limit: Number(risk.position_limit),
        kill_switch: !!risk.kill_switch,
      });
      toast.success("Risk policy updated");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell>
      <PageHeader
        overline="Settings"
        title="Account · risk · alerts"
        description="Tune daily caps, drawdown limits, broker keys and where critical alerts are delivered."
      />

      <div className="p-8">
        <Tabs defaultValue="risk" className="w-full">
          <TabsList className="rounded-none bg-[var(--bg-surface)] border border-[var(--border)] h-auto p-0 mb-6 flex-wrap">
            <TabsTrigger value="risk" data-testid="settings-tab-risk" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3">
              <ShieldCheck className="w-4 h-4 mr-2" /> RISK & PROFILE
            </TabsTrigger>
            <TabsTrigger value="alerts" data-testid="settings-tab-alerts" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3">
              <Bell className="w-4 h-4 mr-2" /> ALERTS
            </TabsTrigger>
            <TabsTrigger value="security" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3">
              <Lock className="w-4 h-4 mr-2" /> SECURITY
            </TabsTrigger>
            <TabsTrigger value="desktop" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3">
              <Monitor className="w-4 h-4 mr-2" /> DESKTOP APP
            </TabsTrigger>
            <TabsTrigger value="demo" className="rounded-none data-[state=active]:bg-[var(--bg-surface-2)] data-[state=active]:text-white font-section tracking-wider px-6 py-3">
              <Wallet className="w-4 h-4 mr-2" /> DEMO ACCOUNT
            </TabsTrigger>
          </TabsList>

          {/* ── Risk & Profile ── */}
          <TabsContent value="risk">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="panel p-6">
                <div className="overline mb-3">Profile</div>
                <div className="space-y-3">
                  <div>
                    <Label className="overline">Email</Label>
                    <div className="font-mono-data text-sm mt-1">{user?.email}</div>
                  </div>
                  <div>
                    <Label className="overline">Name</Label>
                    <div className="font-section text-sm mt-1">{user?.name}</div>
                  </div>
                  <div>
                    <Label className="overline">Role</Label>
                    <div className="font-section text-sm mt-1 txt-warn uppercase">{user?.role}</div>
                  </div>
                  {user?.subscription_tier && (
                    <div>
                      <Label className="overline">Subscription</Label>
                      <div className={`font-section text-sm mt-1 uppercase ${user.subscription_tier === "pro" ? "txt-profit" : user.subscription_tier === "enterprise" ? "text-purple-400" : "txt-muted"}`}>
                        {user.subscription_tier}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="panel p-6">
                <div className="overline mb-3">Risk policy</div>
                {risk && (
                  <div className="space-y-4">
                    <div>
                      <Label className="overline">Max drawdown (%)</Label>
                      <Input data-testid="risk-max-dd" type="number" value={risk.max_drawdown_pct} onChange={(e) => setRisk({ ...risk, max_drawdown_pct: e.target.value })} className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data" />
                    </div>
                    <div>
                      <Label className="overline">Daily loss cap (₹)</Label>
                      <Input data-testid="risk-daily-cap" type="number" value={risk.daily_loss_cap} onChange={(e) => setRisk({ ...risk, daily_loss_cap: e.target.value })} className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data" />
                    </div>
                    <div>
                      <Label className="overline">Position limit</Label>
                      <Input data-testid="risk-pos-limit" type="number" value={risk.position_limit} onChange={(e) => setRisk({ ...risk, position_limit: e.target.value })} className="mt-2 rounded-none bg-[var(--bg-page)] border-[var(--border)] font-mono-data" />
                    </div>
                    <div className="flex items-center justify-between border-t border-[var(--border)] pt-4">
                      <div>
                        <div className="overline">Kill switch</div>
                        <div className="text-xs txt-muted mt-1">Blocks all new orders when ON.</div>
                      </div>
                      <Switch data-testid="settings-kill-switch" checked={!!risk.kill_switch} onCheckedChange={(v) => setRisk({ ...risk, kill_switch: v })} />
                    </div>
                    <Button data-testid="risk-save-btn" onClick={save} disabled={saving} className="rounded-none bg-white text-black hover:bg-zinc-200 font-section tracking-wider w-full h-10">
                      <Save className="w-4 h-4 mr-2" /> {saving ? "SAVING…" : "SAVE POLICY"}
                    </Button>
                  </div>
                )}
              </div>

              <div className="panel p-6 lg:col-span-2">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="w-4 h-4 txt-muted" />
                  <div className="overline">Live daily loss cap</div>
                </div>
                <LiveLossCapPanel />
              </div>

              {user?.role === "admin" && (
                <div className="panel p-6 lg:col-span-2">
                  <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="w-4 h-4 txt-muted" />
                    <div className="overline">Live derivative entries (admin)</div>
                  </div>
                  <LiveDerivativeSwitchesPanel />
                </div>
              )}

              <div className="panel p-6 lg:col-span-2">
                <div className="flex items-center gap-2 mb-3">
                  <KeyRound className="w-4 h-4 txt-muted" />
                  <div className="overline">Broker connections</div>
                </div>
                <p className="txt-secondary text-sm">
                  Real-money execution requires broker API credentials (KYC + 2FA). Manage your encrypted vault from the
                  <a href="/brokers" className="text-white underline underline-offset-4 ml-1">Brokers</a> page. Supported:
                  <span className="font-section text-white"> Zerodha</span>, <span className="font-section text-white">Upstox</span>, Dhan, ICICI Direct and Rmoney.
                </p>
              </div>
            </div>
          </TabsContent>

          {/* ── Alerts ── */}
          <TabsContent value="alerts">
            <AlertsPanel />
          </TabsContent>

          {/* ── Security ── */}
          <TabsContent value="security">
            <div className="space-y-6">
              <PasswordPanel />
              {user?.role === "signal_user" && <PromotionPanel user={user} />}
            </div>
          </TabsContent>

          {/* ── Desktop App ── */}
          <TabsContent value="desktop">
            <DownloadPanel />
          </TabsContent>

          {/* ── Demo Account ── */}
          <TabsContent value="demo">
            <DemoAccountPanel />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}
